/* ═══════════════════════════════════════════════════════════════════════════
   Le hitstop, et surtout son budget glissant.

   Le hitstop est un gel bref de la simulation à l'instant d'un impact. C'est
   l'un des deux ou trois leviers qui font qu'un tir « pèse » quelque chose, et
   il coûte trois lignes. Le budget, lui, est ce qui l'empêche de détruire le
   jeu.

   Sans plafond, huit ennemis qui meurent en 400 ms produisent 176 ms de gel
   cumulé : la partie devient un diaporama au moment précis où elle devrait être
   la plus lisible, et la latence perçue explose. Le plan (§6.2) impose donc
   90 ms de hitstop cumulé maximum par fenêtre glissante d'une seconde.

   Implémenté en seau percé : le budget se vide à 90 ms par seconde, donc à
   0,09 ms par milliseconde. Pas d'historique à stocker, pas d'allocation, et
   un comportement continu plutôt qu'un à-coup tous les 1 000 ms — une fenêtre
   discrète ferait repartir le budget à neuf d'un coup, ce que le joueur
   sentirait comme une irrégularité.

   ⚠️ Ce module vit dans src/game/ et non dans src/sim/ : le hitstop ne modifie
   aucun état de jeu, il décide seulement si l'on exécute un tick ou pas. La
   simulation reste une fonction pure de (état, entrées, tick), et un replay
   enregistre les entrées PAR TICK EXÉCUTÉ — le gel n'a donc rien à y faire.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Millisecondes de gel autorisées par fenêtre glissante d'une seconde. */
export const HITSTOP_BUDGET_MS = 90;
const LEAK_PER_MS = HITSTOP_BUDGET_MS / 1000;

export class Hitstop {
  /** Gel restant à consommer, en millisecondes. */
  private remaining = 0;
  /** Budget déjà dépensé dans la fenêtre glissante. */
  private spent = 0;

  get active(): boolean {
    return this.remaining > 0;
  }
  get remainingMs(): number {
    return this.remaining;
  }
  /** Part du budget consommée, dans [0,1]. Utile en surcouche de debug. */
  get load(): number {
    return this.spent / HITSTOP_BUDGET_MS;
  }

  /** Demande un gel. Retourne la durée RÉELLEMENT accordée, qui peut être nulle
   *  si le budget est épuisé. Le refus est silencieux et voulu : c'est le
   *  mécanisme qui protège la fluidité quand ça meurt de partout. */
  request(ms: number): number {
    if (ms <= 0) return 0;
    const allowed = HITSTOP_BUDGET_MS - this.spent;
    if (allowed <= 0) return 0;
    const granted = ms < allowed ? ms : allowed;
    this.remaining += granted;
    this.spent += granted;
    return granted;
  }

  /** Consomme `dtMs` de temps réel. Retourne `true` si la simulation doit être
   *  GELÉE pour ce pas — l'appelant saute alors son `step`. */
  consume(dtMs: number): boolean {
    // Le budget fuit en temps réel, gel compris : sinon un gel long
    // n'autoriserait aucune reconstitution pendant sa propre durée et le
    // mécanisme s'auto-affamerait.
    this.spent -= dtMs * LEAK_PER_MS;
    if (this.spent < 0) this.spent = 0;

    if (this.remaining <= 0) return false;
    this.remaining -= dtMs;
    if (this.remaining < 0) this.remaining = 0;
    return true;
  }

  reset(): void {
    this.remaining = 0;
    this.spent = 0;
  }
}
